import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import iconRemove from '../../../assets/icons/ic_remove.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/ic_search.svg';
import { useDictionary } from '../../../utilities/hooks/UseDictionary';
import { searchCustomers } from '../components/provider_customers/networking';

const MIN_QUERY_LENGTH = 3;

/**
 * Search field that looks up existing customers and shows a results dropdown
 * (same interaction as the check-in booking search). Calls `onSelect` with the
 * chosen customer, or `null` whenever the query changes / is cleared.
 */
const CustomerSearchInput = ({ onSelect, placeholder, testId }) => {
	const dictionary = useDictionary();
	const [query, setQuery] = useState('');
	const [results, setResults] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isEmpty, setIsEmpty] = useState(false);
	const [focused, setFocused] = useState(false);

	const fetchResults = async (value) => {
		try {
			setIsLoading(true);
			setIsEmpty(false);
			const { data } = await searchCustomers(value);
			const items = data?.data || [];
			setResults(items);
			setIsEmpty(items.length === 0);
		} catch (error) {
			setResults([]);
			setIsEmpty(false);
		} finally {
			setIsLoading(false);
		}
	};

	// Debounced so we only hit the API once the provider pauses typing.
	const debouncedFetch = useMemo(() => debounce(fetchResults, 400), []);

	const handleChange = (event) => {
		const value = event.target.value;
		setQuery(value);
		// Any edit invalidates a previously picked customer.
		onSelect(null);

		if (value.trim().length >= MIN_QUERY_LENGTH) {
			debouncedFetch(value.trim());
		} else {
			setResults([]);
			setIsEmpty(false);
		}
	};

	const handleClear = () => {
		setQuery('');
		setResults([]);
		setIsEmpty(false);
		onSelect(null);
	};

	const handlePick = (customer) => {
		setQuery(customer.name || '');
		setResults([]);
		setIsEmpty(false);
		onSelect(customer);
	};

	const showDropdown =
		focused && query.trim().length >= MIN_QUERY_LENGTH;

	return (
		<div className="customer-search">
			<div className="customer-search-field">
				<SearchIcon className="customer-search-icon" />
				<input
					type="text"
					className="customer-search-input"
					value={query}
					placeholder={placeholder}
					onChange={handleChange}
					onFocus={() => setFocused(true)}
					onBlur={() => setFocused(false)}
					data-testid={testId}
				/>
				{query !== '' ? (
					<button
						type="button"
						className="customer-search-clear"
						onClick={handleClear}
						aria-label={dictionary.clear}
					>
						<img src={iconRemove} alt="" />
					</button>
				) : null}
			</div>

			{showDropdown ? (
				<div className="customer-search-results">
					{isLoading ? (
						<div className="customer-search-message">
							{dictionary.searching}
						</div>
					) : null}

					{!isLoading &&
						results.map((customer) => (
							// onMouseDown fires before the input's blur, so the
							// dropdown is still open when the pick is handled.
							<button
								type="button"
								key={customer.id}
								className="customer-search-result"
								onMouseDown={() => handlePick(customer)}
							>
								<span className="customer-search-result-name">
									{customer.name}
								</span>
								{customer.email || customer.phone ? (
									<span className="customer-search-result-meta">
										{customer.email ? (
											<span>{customer.email}</span>
										) : null}
										{customer.email && customer.phone ? (
											<span className="customer-search-result-dot">
												·
											</span>
										) : null}
										{customer.phone ? (
											<span>{customer.phone}</span>
										) : null}
									</span>
								) : null}
							</button>
						))}

					{!isLoading && isEmpty ? (
						<div className="customer-search-message">
							{dictionary.no_customers_found}
						</div>
					) : null}
				</div>
			) : null}
		</div>
	);
};

CustomerSearchInput.propTypes = {
	onSelect: PropTypes.func.isRequired,
	placeholder: PropTypes.string,
	testId: PropTypes.string
};

CustomerSearchInput.defaultProps = {
	placeholder: '',
	testId: undefined
};

export default CustomerSearchInput;
